import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Observable, firstValueFrom, from } from 'rxjs';

export type SupportedLanguage = 'en' | 'de';

@Injectable({
  providedIn: 'root',
})
export class TranslationService {
  private readonly currentLang = new BehaviorSubject<SupportedLanguage>('en');

  // Public observables
  readonly currentLang$: Observable<SupportedLanguage> = this.currentLang.asObservable();

  // Supported languages
  private readonly supportedLanguages: SupportedLanguage[] = ['en', 'de'];

  // Storage key for consistency
  private readonly STORAGE_KEY = 'selectedLanguage';

  // Language mappings for browser language variants
  private readonly languageMap: Record<string, SupportedLanguage> = {
    // English variants
    en: 'en',
    'en-US': 'en',
    'en-GB': 'en',
    'en-CA': 'en',
    'en-AU': 'en',
    'en-NZ': 'en',
    'en-IE': 'en',
    'en-ZA': 'en',

    // German variants
    de: 'de',
    'de-DE': 'de',
    'de-AT': 'de',
    'de-CH': 'de',
    'de-LU': 'de',
    'de-LI': 'de',
  };

  constructor(private translate: TranslateService) {
    this.initializeTranslation();
  }

  /**
   * @description Initializes the translation system
   */
  private async initializeTranslation(): Promise<void> {
    try {
      // Set available languages
      this.translate.addLangs(this.supportedLanguages);

      // Set default language
      this.translate.setDefaultLang('en');

      // Determine initial language
      const initialLang = this.determineInitialLanguage();

      // Try to load the translations first, then set the language
      await this.loadAndSetLanguage(initialLang);
    } catch (error) {
      console.warn('Error initializing translations, falling back to English:', error);
      // Fallback to English if translation loading fails
      this.applyLanguage('en');
    }
  }

  /**
   * @description Loads translations and sets the language
   * @param lang - Language to load and set
   */
  private async loadAndSetLanguage(lang: SupportedLanguage): Promise<void> {
    try {
      console.log(`🌍 Loading translations for: ${lang}`);
      // Load the translation file using use() which loads and sets the language
      await firstValueFrom(this.translate.use(lang));
      console.log(`✅ Successfully loaded translations for: ${lang}`);
      // Update internal state after successful loading
      this.currentLang.next(lang);
      this.storeLanguageSafely(lang);
    } catch (error) {
      console.warn(`❌ Failed to load translations for ${lang}, falling back to English:`, error);

      // Try to load English as fallback
      if (lang !== 'en') {
        try {
          console.log('🔄 Attempting to load English as fallback...');
          await firstValueFrom(this.translate.use('en'));
          console.log('✅ Successfully loaded English translations as fallback');
          this.currentLang.next('en');
          this.storeLanguageSafely('en');
        } catch (fallbackError) {
          console.warn('❌ Failed to load English translations as well:', fallbackError);
          // Even if loading fails, set the language so the app doesn't break
          this.applyLanguage('en');
        }
      } else {
        // Even if loading fails, set the language so the app doesn't break
        this.applyLanguage('en');
      }
    }
  }

  /**
   * @description Determines the initial language based on storage and browser detection
   * @returns {SupportedLanguage} The language to use
   */
  private determineInitialLanguage(): SupportedLanguage {
    // 1. Check localStorage first (user preference)
    const storedLang = this.getStoredLanguageSafely();
    if (storedLang && this.isLanguageSupported(storedLang)) {
      return storedLang as SupportedLanguage;
    }

    // 2. Fallback to browser language detection
    return this.detectBrowserLanguage();
  }

  /**
   * @description Safely gets language from localStorage
   * @returns {string | null} Stored language or null
   */
  private getStoredLanguageSafely(): string | null {
    try {
      return localStorage.getItem(this.STORAGE_KEY);
    } catch {
      // Silent fail - localStorage might be disabled (private browsing, etc.)
      return null;
    }
  }

  /**
   * @description Safely stores language in localStorage
   * @param lang - Language to store
   */
  private storeLanguageSafely(lang: SupportedLanguage): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, lang);
    } catch {
      // Silent fail - localStorage might be disabled
    }
  }

  /**
   * @description Detects the browser language and maps it to supported languages
   * @returns {SupportedLanguage} The mapped supported language
   */
  private detectBrowserLanguage(): SupportedLanguage {
    try {
      // Get all possible language sources (already filtered for truthy values)
      const languageSources = [
        this.translate.getBrowserLang(),
        navigator.language,
        ...(navigator.languages || []),
      ].filter((lang): lang is string => Boolean(lang));

      // Try to find a match in our language map
      for (const lang of languageSources) {
        // Check exact match first
        if (this.languageMap[lang]) {
          return this.languageMap[lang];
        }

        // Check language prefix (e.g., 'en' from 'en-US')
        const langPrefix = lang.split('-')[0];
        if (this.languageMap[langPrefix]) {
          return this.languageMap[langPrefix];
        }
      }
    } catch {
      // Silent fail for browser detection errors
    }

    // Fallback to English for all other cases
    return 'en';
  }

  /**
   * @description Applies the language without validation (internal use)
   * @param lang - The validated language to apply
   */
  private applyLanguage(lang: SupportedLanguage): void {
    this.translate.use(lang);
    this.currentLang.next(lang);
    this.storeLanguageSafely(lang);
  }

  /**
   * @description Sets the language for the application
   * @param lang - The language to set
   */
  setLanguage(lang: string): Observable<void> {
    const normalizedLang = this.normalizeLanguage(lang);

    if (!this.isLanguageSupported(normalizedLang)) {
      console.warn(`Language '${lang}' is not supported. Falling back to 'en'.`);
      return from(this.loadAndSetLanguage('en'));
    }

    return from(this.loadAndSetLanguage(normalizedLang as SupportedLanguage));
  }

  /**
   * @description Normalizes language input (handles case sensitivity, etc.)
   * @param lang - Raw language input
   * @returns {string} Normalized language
   */
  private normalizeLanguage(lang: string): string {
    if (!lang || typeof lang !== 'string') {
      return 'en';
    }
    return lang.toLowerCase().trim();
  }

  /**
   * @description Gets the current language
   * @returns {SupportedLanguage} The current language
   */
  getCurrentLang(): SupportedLanguage {
    return this.currentLang.value;
  }

  /**
   * @description Gets the list of supported languages
   * @returns {SupportedLanguage[]} Array of supported language codes
   */
  getSupportedLanguages(): SupportedLanguage[] {
    return [...this.supportedLanguages];
  }

  /**
   * @description Checks if a language is supported
   * @param lang - The language code to check
   * @returns {boolean} True if language is supported
   */
  isLanguageSupported(lang: string): boolean {
    return this.supportedLanguages.includes(lang as SupportedLanguage);
  }

  /**
   * @description Gets the mapped language for a browser language variant
   * @param browserLang - The browser language code
   * @returns {SupportedLanguage} The mapped supported language
   */
  getMappedLanguage(browserLang: string): SupportedLanguage {
    if (!browserLang) return 'en';

    // Check exact match first
    if (this.languageMap[browserLang]) {
      return this.languageMap[browserLang];
    }

    // Check language prefix
    const langPrefix = browserLang.split('-')[0];
    return this.languageMap[langPrefix] || 'en';
  }

  /**
   * @description Gets language display name for UI
   * @param lang - Language code
   * @returns {string} Display name
   */
  getLanguageDisplayName(lang: SupportedLanguage): string {
    const displayNames: Record<SupportedLanguage, string> = {
      en: 'English',
      de: 'Deutsch',
    };
    return displayNames[lang] || lang;
  }

  /**
   * @description Resets language preference and detects browser language
   */
  async resetToDefault(): Promise<void> {
    try {
      localStorage.removeItem(this.STORAGE_KEY);
    } catch {
      // Silent fail for localStorage errors
    }

    const detectedLang = this.detectBrowserLanguage();
    await this.loadAndSetLanguage(detectedLang);
  }

  /**
   * @description Debug method for console testing
   * Call from console: window.debugTranslations()
   */
  debug(): void {
    console.group('🔍 Translation Service Debug Info');
    console.log('Current Language:', this.getCurrentLang());
    console.log('Supported Languages:', this.getSupportedLanguages());
    console.log('Browser Language:', navigator.language);
    console.log('Browser Languages:', navigator.languages);
    console.log('Stored Language:', this.getStoredLanguageSafely());
    console.log('Detected Language:', this.detectBrowserLanguage());

    // Test a simple translation
    const testKey = 'modules.onboarding.welcome.title';
    const translation = this.translate.instant(testKey);
    console.log(`Test Translation (${testKey}):`, translation);
    console.log('Translation Working:', translation !== testKey);

    console.groupEnd();
  }
}
